import { Images } from "../../../../config/images";
import TopImage from "../../common/TopImage";
import CustomInput from "../../../common/CustomInput";
import { useRef, useState, useEffect } from "react";
import { Form, Formik } from "formik";
import { useUpdateStudent } from "../../../../hooks/school/student/useUpdateStudent";
import { useParams } from "react-router-dom";
import BackButton from "../Goback/GoBack";
import { Alert, Spinner, Button } from "react-bootstrap";
import { useGetStudentDetail } from "../../../../hooks/teacher/students/useGetStudentDetail";
import { createStudentValidationSchema } from "../../SchoolAdmin/Student/CreateStudent";
import SuccessModal from "../../../common/SuccessModal";
import { useToggleStudentStatus } from "../../../../hooks/school/student/useToggleStudentStatus";
import DeleteModal from "../../../common/DeleteModal";
import "../../SuperAdmin/toggle.css";

const UpdateStudentDetail = () => {
  const firstNameRef = useRef(null);
  const { id } = useParams();

  const {
    student,
    loading: fetchLoading,
    error: fetchError,
    getStudentDetail,
    setStudent,
  } = useGetStudentDetail();
  const {
    updateStudent,
    loading: updateLoading,
    error: updateError,
  } = useUpdateStudent();
  const {
    toggleStudentStatus,
    loading: toggleLoading,
    error: toggleError,
    success: toggleSuccess,
    resetState: resetToggleState,
  } = useToggleStudentStatus();

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showToggleModal, setShowToggleModal] = useState(false);

  const isActive = student?.status === "1" || student?.status === 1;

  useEffect(() => {
    if (id) {
      getStudentDetail({ studentId: id });
    }
  }, [id]);

  useEffect(() => {
    if (!fetchLoading && student) {
      firstNameRef.current?.focus();
    }
  }, [fetchLoading, student]);

  /**
   * Parse student name into firstName and lastName
   * @param {string} fullName
   * @returns {{ firstName: string, lastName: string }}
   */
  const parseStudentName = (fullName) => {
    if (!fullName) return { firstName: "", lastName: "" };
    const nameParts = fullName.trim().split(" ");
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";
    return { firstName, lastName };
  };

  const getInitialValues = () => {
    if (!student) {
      return {
        firstName: "",
        lastName: "",
        parent_email: "",
        age: "",
        username: "",
      };
    }

    const { firstName, lastName } = parseStudentName(student.name);
    return {
      firstName,
      lastName,
      parent_email: student.email || "",
      age: student.age || "",
      username: student.username || "",
    };
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const updatedData = {
        name: `${values.firstName} ${values.lastName}`,
        username: values.username,
        assigned_to: Number(values.assigned_to) || null,
        parent_email: values.parent_email,
        age: values.age,
      };

      await updateStudent({
        studentId: id,
        updatedData,
        onSuccess: () => {
          setShowSuccessModal(true);
        },
      });
    } catch (error) {
      // Error is handled by the hook
    } finally {
      setSubmitting(false);
    }
  };

  //
  // const handleSubmit = async (values) => {
  //   setSuccessMessage("");
  //
  //
  //   const originalValues = getInitialValues();
  //   const changedFields = getChangedFields(values, originalValues);
  //
  //   // Check if there are any changes
  //   if (Object.keys(changedFields).length === 0) {
  //     setSuccessMessage("No changes detected.");
  //     return;
  //   }
  //
  //   await updateStudent({
  //     studentId: id,
  //     updatedData: changedFields,
  //   });
  //   setStudent({
  //     ...student,
  //     ...changedFields,
  //   });
  //
  //   setSuccessMessage("Student details updated successfully!");
  //   setShowSuccessModal(true);
  // };
  //
  // Toggle handlers
  const handleToggleShow = () => {
    resetToggleState();
    setShowToggleModal(true);
  };

  const handleToggleClose = () => {
    resetToggleState();
    setShowToggleModal(false);
  };

  const handleToggle = async () => {
    await toggleStudentStatus(id);
  };

  const handleToggleSuccessContinue = () => {
    setShowToggleModal(false);
    resetToggleState();
    // Update student status locally
    setStudent({
      ...student,
      status: isActive ? "0" : "1",
    });
  };

  if (fetchLoading) {
    return (
      <div>
        <BackButton />
        <TopImage imageSrc={Images.TopImage} text="Update Student" />
        <div className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading student details...</p>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div>
        <BackButton />
        <TopImage imageSrc={Images.TopImage} text="Update Student" />
        <div className="custom-card mt-4">
          <Alert variant="danger">{fetchError}</Alert>
          <button
            className="custom-primary-button mt-3"
            onClick={() => getStudentDetail({ studentId: id })}
          >
            <i className="bi bi-arrow-clockwise me-2"></i>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div>
        <BackButton />
        <TopImage imageSrc={Images.TopImage} text="Update Student" />
        <div className="custom-card mt-4">
          <Alert variant="warning">Student not found.</Alert>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="">
        <BackButton />
      </div>
      <TopImage imageSrc={Images.TopImage} text="Update Student" />

      {/* Status Toggle */}
      <div className="d-flex justify-content-end align-items-center gap-2 pt-3 pb-2">
        <span className={`badge ${isActive ? "bg-success" : "bg-danger"}`}>
          {isActive ? "Active" : "Inactive"}
        </span>
        <div className="toggle-switch">
          <input
            type="checkbox"
            id={`switch-${student.id}`}
            checked={isActive}
            onChange={handleToggleShow}
          />
          <label
            htmlFor={`switch-${student.id}`}
            className="slider"
            style={{
              backgroundColor: isActive ? "#008EF4" : "",
            }}
          ></label>
        </div>
      </div>

      <div className="custom-card mt-2">
        <Formik
          initialValues={getInitialValues()}
          validationSchema={createStudentValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form className="d-flex flex-column gap-3">
              <div className="row g-3">
                <div className="col-md-6 ">
                  <CustomInput
                    label="First Name"
                    name="firstName"
                    type="text"
                    placeholder="Enter first name"
                    className="custom-primary-input"
                    layout="stacked"
                    ref={firstNameRef}
                  />
                </div>
                <div className="col-md-6">
                  <CustomInput
                    label="Last Name"
                    name="lastName"
                    type="text"
                    placeholder="Enter last name"
                    className="custom-primary-input"
                    layout="stacked"
                  />
                </div>

                <div className="col-md-6">
                  <CustomInput
                    label="School Registered ID"
                    name="username"
                    type="string"
                    placeholder="Enter School Registered ID"
                    className="custom-primary-input"
                    layout="stacked"
                  />
                </div>

                <div className="col-md-6">
                  <CustomInput
                    label="Age"
                    name="age"
                    type="number"
                    placeholder="Enter age"
                    className="custom-primary-input"
                    layout="stacked"
                  />
                </div>
              </div>

              <CustomInput
                label="Parent Email Address"
                name="parent_email"
                type="email"
                placeholder="Enter parent email address"
                className="custom-primary-input"
                layout="stacked"
                disabled={true}
              />
              <div>
                {updateError && (
                  <div className="custom-error pb-2">{updateError}</div>
                )}

                <Button
                  type="submit"
                  className="custom-primary-button"
                  disabled={isSubmitting || updateLoading}
                >
                  {isSubmitting || updateLoading ? "Updating..." : "Update"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      <SuccessModal
        showModal={showSuccessModal}
        handleClose={() => setShowSuccessModal(false)}
        message="The student details have been updated successfully."
        title="Success"
        primaryButtonText="Continue Editing"
        secondaryButtonText="Student List"
        onPrimaryClick={() => setShowSuccessModal(false)}
        secondaryButtonLink="/teacher/student"
      />

      <DeleteModal
        showModal={showToggleModal}
        handleClose={handleToggleClose}
        handleDelete={handleToggle}
        message={`Are you sure you want to ${isActive ? "deactivate" : "activate"} ${student?.name || "this student"}?`}
        loading={toggleLoading}
        error={toggleError}
        success={toggleSuccess}
        successMessage={`${student?.name || "Student"} has been ${isActive ? "deactivated" : "activated"} successfully.`}
        onSuccessContinue={handleToggleSuccessContinue}
        customSuccessTitle={
          isActive ? "Deactivated Successfully" : "Activated Successfully"
        }
        customErrorTitle="Toggle Failed"
        customConfirmTitle="Confirm Status Change"
        customActionButtonText={isActive ? "Deactivate" : "Activate"}
        customLoadingButtonText={isActive ? "Deactivating..." : "Activating..."}
      />
    </div>
  );
};

export default UpdateStudentDetail;
